import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Settings,
  Users,
  Calendar,
  BarChart3,
  Building2,
  Activity,
  AlertTriangle,
  TrendingUp,
  Database,
  FileQuestion,
  Library,
  ClipboardList,
  RefreshCw,
  UserCheck,
  UserPlus,
  UserMinus,
  Download,
  MessageCircleQuestion,
  MessageSquare,
  Menu,
  BookOpen,
  Megaphone,
  RotateCcw,
  Wrench,
  Grid3x3,
  FlaskConical,
  LucideIcon,
  ClipboardPen,
  ShoppingBag,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import SystemConfiguration from '@/components/admin/SystemConfiguration';
import PerformanceCycleAdmin from '@/components/admin/PerformanceCycleAdmin';
import UserManagement from '@/components/admin/UserManagement';
import IntegrationManagement from '@/components/admin/IntegrationManagement';
import QuestionBankPage from './QuestionBankPage';
import TemplateManagementPage from './TemplateManagementPage';
import FeedbackCampaignsPage from './FeedbackCampaignsPage';
import StaffOrderAdminPage from './StaffOrderAdminPage';
// HRDataCorrections moved to apps/hr-ops.
import FeedbackRequestsAdmin from '@/components/admin/FeedbackRequestsAdmin';
import ReviewReopenAdmin from '@/components/admin/ReviewReopenAdmin';
import RecordCheckinOnBehalfAdmin from '@/components/admin/RecordCheckinOnBehalfAdmin';
import CalibrationFixesAdmin from '@/components/admin/CalibrationFixesAdmin';
import SuccessionRoleManagement from '@/components/admin/SuccessionRoleManagement';
import TalentGrid from '@/components/performance/manager/talent-review/TalentGrid';
import HipoWorklist from '@/components/performance/manager/talent-review/HipoWorklist';
import FODataExportPage from './FODataExportPage';
import SupportAdminPanel from '@/components/support/SupportAdminPanel';
import UsageAdoptionDashboard from '@/components/admin/UsageAdoptionDashboard';
import UatPersonaSeedAdmin, { PERSONA_ENVIRONMENTS, currentEnvironment } from '@/components/admin/UatPersonaSeedAdmin';
import TalentReviewResetAdmin from '@/components/admin/TalentReviewResetAdmin';
import CalibrationTestResetAdmin from '@/components/admin/CalibrationTestResetAdmin';
// DataQualityPage moved to apps/hr-ops.
// NewHirePage + EmployeeLifecyclePage moved to apps/hr-ops (HR Operations app)
// under /hr-ops/new-hire and /hr-ops/lifecycle. Linked from the top-bar
// below via the "HR Operations" cross-app entry point.
import { adminService } from '@/services/api/adminService';
import { useAuth } from '@/providers/AuthProvider';
import { hasAnyPermission, STAFF_ORDER_KEYS } from '@/auth/permissions';
import { EXPORT_PERMISSION_KEYS, EXPORT_ROLES } from '@/lib/exportPermissions';
import { CALIBRATION_REMEDIATION_KEYS, CALIBRATION_REMEDIATION_ROLES } from '@/lib/calibrationRemediationPermissions';

interface QuickStat {
  label: string;
  value: string | number;
  trend?: string;
  status?: 'success' | 'warning' | 'error';
  icon: React.ReactNode;
}

type AdminTabGroup = 'overview' | 'system' | 'content' | 'operations' | 'data-support';

interface AdminTabConfig {
  id: string;
  label: string;
  icon: LucideIcon;
  roles: string[];
  /** Capability keys that also unlock this tab, for users holding no listed role. */
  permissions?: readonly string[];
  /** Environments the tab exists in (VITE_ENVIRONMENT); every environment when absent. */
  environments?: readonly string[];
  group: AdminTabGroup;
}

const ADMIN_GROUPS: { id: AdminTabGroup; label: string }[] = [
  { id: 'overview', label: '' },
  { id: 'system', label: 'System' },
  { id: 'content', label: 'Content' },
  { id: 'operations', label: 'Operations' },
  { id: 'data-support', label: 'Data & Support' },
];

// Tab visibility configuration per role
const ADMIN_TAB_CONFIG: AdminTabConfig[] = [
  // Overview
  { id: 'overview', label: 'Overview', icon: BarChart3, roles: ['admin', 'hr_admin'], group: 'overview' },
  // System
  { id: 'config', label: 'KPA Framework', icon: BookOpen, roles: ['admin', 'hr_admin'], group: 'system' },
  { id: 'cycle', label: 'Cycles', icon: Calendar, roles: ['admin', 'hr_admin'], group: 'system' },
  { id: 'users', label: 'Users', icon: Users, roles: ['admin', 'hr_admin'], group: 'system' },
  { id: 'integration', label: 'Integration', icon: Database, roles: ['admin', 'hr_admin'], group: 'system' },
  // Reset the Annual I&G Review (16 Sep 2026) and rebuild the testers' personas (15 Sep 2026):
  // admin and hr_admin only, and only where test personas exist. The server applies the same
  // rule to the signed-in person.
  { id: 'test-data', label: 'Test data', icon: FlaskConical, roles: ['admin', 'hr_admin'], environments: PERSONA_ENVIRONMENTS, group: 'system' },
  // Content
  { id: 'question-bank', label: 'Question Bank', icon: FileQuestion, roles: ['admin', 'hr_admin'], group: 'content' },
  { id: 'templates', label: 'Templates', icon: Library, roles: ['admin', 'hr_admin'], group: 'content' },
  { id: 'feedback-campaigns', label: 'Feedback Campaigns', icon: Megaphone, roles: ['admin', 'hr_admin'], group: 'content' },
  // Operations — 'corrections' moved to apps/hr-ops.
  { id: 'feedback-requests', label: 'Feedback Requests', icon: MessageSquare, roles: ['admin', 'hr_admin'], group: 'operations' },
  { id: 'reopen-reviews', label: 'Reopen Reviews', icon: RotateCcw, roles: ['admin', 'hr_admin'], group: 'operations' },
  // HR UAT list #24: HRBPs record a check-in held outside the system, on the People Leader's behalf.
  { id: 'record-checkin', label: 'Record a Check-in', icon: ClipboardPen, roles: ['admin', 'hr_admin', 'hrbp'], permissions: ['review:record-on-behalf'], group: 'operations' },
  // admin/hr_admin by role; a named grant admits anyone else without the role.
  { id: 'calibration-fixes', label: 'Calibration Fixes', icon: Wrench, roles: [...CALIBRATION_REMEDIATION_ROLES], permissions: CALIBRATION_REMEDIATION_KEYS, group: 'operations' },
  { id: 'succession', label: 'Succession', icon: UserCheck, roles: ['admin', 'hr_admin'], group: 'operations' },
  // The published talent map, organisation-wide (HR sees every placement).
  { id: 'talent-map', label: 'Talent map', icon: Grid3x3, roles: ['admin', 'hr_admin', 'hrbp'], group: 'operations' },
  // Staff apparel ordered against a personal voucher: order rounds, vouchers, emails, the orders download.
  // admin/hr_admin by role; the order is Finance's, so its people come in by the staff_order:manage grant.
  { id: 'staff-order', label: 'Staff order', icon: ShoppingBag, roles: ['admin', 'hr_admin'], permissions: [...STAFF_ORDER_KEYS], group: 'operations' },
  // 'new-hire' + 'lifecycle' moved to apps/hr-ops (HR Operations app).
  // Data & Support — 'data-quality' moved to apps/hr-ops.
  // admin/hr_admin see every export; anyone else sees the tab only if they hold
  // an export grant, and then only the card(s) that grant covers.
  { id: 'data-export', label: 'Data Export', icon: Download, roles: [...EXPORT_ROLES], permissions: EXPORT_PERMISSION_KEYS, group: 'data-support' },
  { id: 'usage', label: 'Usage & Adoption', icon: TrendingUp, roles: ['admin', 'hr_admin'], group: 'data-support' },
  { id: 'support', label: 'Support', icon: MessageCircleQuestion, roles: ['admin', 'hr_admin', 'hrbp'], group: 'data-support' },
];

const HRAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const userRoles = user?.roles || [];

  const isFullAdmin = userRoles.some(r => r === 'admin' || r === 'hr_admin');

  const visibleTabs = useMemo(() =>
    ADMIN_TAB_CONFIG.filter(tab =>
      (!tab.environments || tab.environments.includes(currentEnvironment())) && (
        tab.roles.some(role => userRoles.includes(role)) ||
        // A per-user grant admits the tab even without one of its roles.
        (!!tab.permissions?.length && hasAnyPermission(user, tab.permissions))
      )
    ),
    [userRoles, user]
  );

  const groupedTabs = useMemo(() =>
    ADMIN_GROUPS
      .map(group => ({ ...group, tabs: visibleTabs.filter(t => t.group === group.id) }))
      .filter(group => group.tabs.length > 0),
    [visibleTabs]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = visibleTabs[0]?.id || 'corrections';
  const urlTab = searchParams.get('tab');
  const activeTab = urlTab && visibleTabs.some(t => t.id === urlTab) ? urlTab : defaultTab;
  const setActiveTab = (value: string) => {
    setSearchParams({ tab: value });
  };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [overview, setOverview] = useState<{
    active_users: number;
    review_completion: number;
    pending_actions: number;
    active_campuses: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  // Talent map year — the current year by default, the last three on offer.
  const currentYear = new Date().getFullYear();
  const [talentMapYear, setTalentMapYear] = useState<number>(currentYear);
  // The map, or the HIPO worklist the Good Governance People Forum works from.
  const [talentMapView, setTalentMapView] = useState<'map' | 'hipos'>('map');
  const talentMapYears = useMemo(
    () => [currentYear, currentYear - 1, currentYear - 2],
    [currentYear]
  );

  useEffect(() => {
    if (!isFullAdmin) {
      setLoading(false);
      return;
    }
    const fetchOverview = async () => {
      try {
        const data = await adminService.getOverview();
        setOverview(data);
      } catch (error) {
        console.error('Failed to fetch admin overview:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, [isFullAdmin]);

  // Quick stats for HR Admin overview - now using real data
  const quickStats: QuickStat[] = [
    {
      label: 'Active Users',
      value: loading ? '...' : (overview?.active_users?.toLocaleString() || '0'),
      trend: 'Total team members',
      status: 'success',
      icon: <Users className="w-5 h-5" />
    },
    {
      label: 'Check-in Completion',
      value: loading ? '...' : `${overview?.review_completion || 0}%`,
      trend: 'Current cycle',
      status: 'success',
      icon: <Activity className="w-5 h-5" />
    },
    {
      label: 'Pending Actions',
      value: loading ? '...' : (overview?.pending_actions?.toString() || '0'),
      trend: 'Requires attention',
      status: (overview?.pending_actions || 0) > 0 ? 'warning' : 'success',
      icon: <AlertTriangle className="w-5 h-5" />
    },
    {
      label: 'Active Campuses',
      value: loading ? '...' : (overview?.active_campuses?.toString() || '0'),
      trend: 'All operational',
      status: 'success',
      icon: <Building2 className="w-5 h-5" />
    }
  ];

  const getStatusColor = (status?: 'success' | 'warning' | 'error') => {
    switch (status) {
      case 'success':
        return 'text-emerald-600';
      case 'warning':
        return 'text-amber-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // --- Sidebar nav helpers (shared between desktop & mobile) ---

  const renderNavItem = (tab: AdminTabConfig, closeMobile?: boolean) => (
    <button
      key={tab.id}
      onClick={() => { setActiveTab(tab.id); if (closeMobile) setSidebarOpen(false); }}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150',
        activeTab === tab.id
          ? 'bg-heritage-blue text-white shadow-sm'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
      )}
      aria-current={activeTab === tab.id ? 'page' : undefined}
    >
      <tab.icon className="w-4 h-4 flex-shrink-0" />
      <span className="truncate">{tab.label}</span>
    </button>
  );

  const renderGroupedNav = (closeMobile?: boolean) => (
    <nav aria-label="Admin navigation" className="space-y-5 py-2">
      {groupedTabs.map((group) => (
        <div key={group.id}>
          {group.label && (
            <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {group.label}
            </h3>
          )}
          <div className="space-y-1">
            {group.tabs.map((tab) => renderNavItem(tab, closeMobile))}
          </div>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">HR Administration</h1>
          <p className="text-gray-600 mt-1">
            {isFullAdmin
              ? 'System configuration, user management, and organisational oversight'
              : 'Data corrections, feedback requests, and support management'}
          </p>
        </div>
      </div>

      {/* Main layout with sidebar navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Mobile sidebar trigger (below lg) */}
        <div className="lg:hidden mb-4">
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm">
                <Menu className="w-4 h-4" />
                <span>{visibleTabs.find(t => t.id === activeTab)?.label || 'Navigate'}</span>
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SheetHeader className="px-4 py-3 border-b">
                <SheetTitle className="text-sm font-semibold">Administration</SheetTitle>
              </SheetHeader>
              <ScrollArea className="h-[calc(100vh-4rem)]">
                <div className="p-3">
                  {renderGroupedNav(true)}
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>
        </div>

        {/* Flex layout: sidebar + content */}
        <div className="flex gap-6">
          {/* Desktop sidebar (hidden below lg) */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-0">
              <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm overflow-y-auto max-h-[calc(100vh-10rem)]">
                {renderGroupedNav()}
              </div>
            </div>
          </aside>

          {/* Content area */}
          <div className="flex-1 min-w-0">
            {/* Quick Stats Grid - only for full admins on overview tab */}
            {isFullAdmin && activeTab === 'overview' && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                {quickStats.map((stat, index) => (
                  <Card key={index} className="p-4 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                        <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                        <p className={`text-xs mt-1 ${getStatusColor(stat.status)}`}>
                          {stat.trend}
                        </p>
                      </div>
                      <div className={`p-2 rounded-lg ${getStatusColor(stat.status)} bg-opacity-10`}>
                        {stat.icon}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6 mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Quick Navigation */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-heritage-blue" />
                    Quick Actions
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'KPA Framework', tab: 'config', icon: BookOpen },
                      { label: 'Manage Users', tab: 'users', icon: Users },
                      { label: 'Check-in Cycles', tab: 'cycle', icon: Calendar },
                      { label: 'Question Bank', tab: 'question-bank', icon: FileQuestion },
                      { label: 'Templates', tab: 'templates', icon: Library },
                      { label: 'Feedback Campaigns', tab: 'feedback-campaigns', icon: Megaphone },
                      { label: 'Staff order', tab: 'staff-order', icon: ShoppingBag },
                      { label: 'Data Corrections', tab: 'corrections', icon: ClipboardList },
                      { label: 'Feedback Requests', tab: 'feedback-requests', icon: MessageSquare },
                      { label: 'Succession Roles', tab: 'succession', icon: UserCheck },
                      { label: 'F&O Integration', tab: 'integration', icon: Database },
                      { label: 'Support & Suggestions', tab: 'support', icon: MessageCircleQuestion },
                    ].map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveTab(item.tab)}
                        className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-heritage-blue transition-colors text-left"
                      >
                        <item.icon className="w-5 h-5 text-heritage-blue" />
                        <span className="text-sm font-medium text-gray-900">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </Card>

                {/* System Status */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-heritage-blue" />
                    System Status
                  </h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Database', status: 'Operational', color: 'emerald' },
                      { label: 'F&O Integration', status: 'Connected', color: 'emerald' },
                      { label: 'Authentication', status: 'Operational', color: 'emerald' },
                      { label: 'API Gateway', status: 'Operational', color: 'emerald' }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                        <span className="text-sm text-gray-700">{item.label}</span>
                        <span className={`text-xs px-2 py-1 rounded-full bg-${item.color}-100 text-${item.color}-700`}>
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Help Section */}
              <Card className="p-6 bg-blue-50 border-blue-200">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Settings className="w-6 h-6 text-heritage-blue" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">HR Administration Portal</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Use the navigation menu to manage users, configure check-in cycles, maintain the question bank,
                      manage objective templates, handle data corrections, and monitor F&O integration status.
                    </p>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* System Configuration Tab */}
            <TabsContent value="config" className="mt-0">
              <SystemConfiguration />
            </TabsContent>

            {/* Performance Cycle Administration Tab */}
            <TabsContent value="cycle" className="mt-0">
              <PerformanceCycleAdmin />
            </TabsContent>

            {/* User Management Tab */}
            <TabsContent value="users" className="mt-0">
              <UserManagement />
            </TabsContent>

            {/* Integration Management Tab */}
            <TabsContent value="integration" className="mt-0">
              <IntegrationManagement />
            </TabsContent>

            {/* Question Bank Tab */}
            <TabsContent value="question-bank" className="mt-0">
              <QuestionBankPage />
            </TabsContent>

            {/* Template Management Tab */}
            <TabsContent value="templates" className="mt-0">
              <TemplateManagementPage />
            </TabsContent>

            {/* Feedback Campaigns Tab */}
            <TabsContent value="feedback-campaigns" className="mt-0">
              <FeedbackCampaignsPage />
            </TabsContent>

            {/* Staff Order Tab — staff apparel against a personal voucher */}
            <TabsContent value="staff-order" className="mt-0">
              <StaffOrderAdminPage />
            </TabsContent>

            {/* Data Corrections moved to apps/hr-ops. */}

            {/* Feedback Requests Admin Tab */}
            <TabsContent value="feedback-requests" className="mt-0">
              <FeedbackRequestsAdmin />
            </TabsContent>

            {/* Reopen Reviews Tab */}
            <TabsContent value="reopen-reviews" className="mt-0">
              <ReviewReopenAdmin />
            </TabsContent>

            {/* Record a check-in held outside the system */}
            <TabsContent value="record-checkin" className="mt-0">
              <RecordCheckinOnBehalfAdmin />
            </TabsContent>

            {/* Calibration Fixes Tab */}
            <TabsContent value="calibration-fixes" className="mt-0">
              <CalibrationFixesAdmin />
            </TabsContent>

            {/* Succession Role Management Tab */}
            <TabsContent value="succession" className="mt-0">
              <SuccessionRoleManagement />
            </TabsContent>

            {/* Talent Map Tab — the published grid for a year, organisation-wide */}
            <TabsContent value="talent-map" className="mt-0">
              <div className="flex items-center justify-end gap-2 mb-4">
                <div className="inline-flex rounded-md border overflow-hidden mr-auto" role="tablist" aria-label="Talent map view">
                  {([['map', 'Talent map'], ['hipos', 'HIPO worklist']] as const).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={talentMapView === id}
                      onClick={() => setTalentMapView(id)}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors ${talentMapView === id ? 'bg-heritage-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <label htmlFor="talent-map-year" className="text-sm font-medium text-gray-700">Year</label>
                <Select value={String(talentMapYear)} onValueChange={(v) => setTalentMapYear(Number(v))}>
                  <SelectTrigger id="talent-map-year" className="w-[110px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {talentMapYears.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {talentMapView === 'map' ? (
                <TalentGrid reviewYear={talentMapYear} title="Talent map" />
              ) : (
                <HipoWorklist reviewYear={talentMapYear} />
              )}
            </TabsContent>

            {/* New Hire + Employment Lifecycle moved to apps/hr-ops. */}

            {/* Data Quality moved to apps/hr-ops. */}

            {/* F&O Data Export Tab */}
            <TabsContent value="data-export" className="mt-0">
              <FODataExportPage />
            </TabsContent>

            {/* Usage & Adoption Analytics Tab */}
            <TabsContent value="usage" className="mt-0">
              <UsageAdoptionDashboard />
            </TabsContent>

            {/* Support & Suggestions Tab */}
            <TabsContent value="support" className="mt-0">
              <SupportAdminPanel />
            </TabsContent>

            {/* Test data: mounted only for those who see the tab, so nobody else asks for the sessions, the testers' plans or the tester list */}
            {visibleTabs.some(t => t.id === 'test-data') && (
              <TabsContent value="test-data" className="mt-0 space-y-6">
                <TalentReviewResetAdmin />
                <CalibrationTestResetAdmin />
                <UatPersonaSeedAdmin />
              </TabsContent>
            )}
          </div>
        </div>
      </Tabs>
    </div>
  );
};

export default HRAdminDashboard;
